import { Injector } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppDialogComponent } from 'src/app/core/components/app-dialog/app-dialog.component';
import {
  AppDialogDismissReason,
  AppDialogOptions,
  AppDialogResult,
  appDialogWidth,
  normalizeAppDialogArgs,
} from './app-dialog-rules';

export { AppDialogDismissReason } from './app-dialog-rules';
export type { AppDialogIcon, AppDialogOptions, AppDialogResult } from './app-dialog-rules';

const DISMISSED: AppDialogResult = { isConfirmed: false, isDenied: false, isDismissed: true };

/**
 * Application dialog — Angular Material, replacing the former alert library (SweetAlert2, removed from the dependencies).
 *
 * Static on purpose, with the call shape the pages already used (`fire`, `close`, `showLoading`,
 * `showValidationMessage`, `getPopup`…), so that the ~200 confirmations, loaders and small forms
 * moved over without being rewritten one by one. New code should prefer a dedicated dialog
 * component when it needs a real form.
 *
 * One dialog at a time, like before: a new `fire()` replaces the open one. The promise resolves
 * when the dialog STARTS closing, while its DOM is still attached, because many pages read the
 * fields of their form in `.then()`.
 */
export class AppDialog {
  static readonly DismissReason = AppDialogDismissReason;

  private static injector: Injector | null = null;
  private static current: { ref: MatDialogRef<AppDialogComponent, AppDialogResult>; component: AppDialogComponent } | null = null;

  /** Called once at bootstrap (app.config.ts). */
  static attach(injector: Injector): void {
    AppDialog.injector = injector;
  }

  static fire<T = any>(options: AppDialogOptions): Promise<AppDialogResult<T>>;
  static fire<T = any>(title?: string, html?: string, icon?: string): Promise<AppDialogResult<T>>;
  static fire<T = any>(...args: any[]): Promise<AppDialogResult<T>> {
    const options = normalizeAppDialogArgs(args);
    if (!AppDialog.injector) {
      console.error('AppDialog used before AppDialog.attach()', options);
      return Promise.resolve({ ...DISMISSED });
    }

    if (options.toast) {
      return AppDialog.toast(options);
    }

    AppDialog.close();

    const dialog = AppDialog.injector.get(MatDialog);
    const panelClass = ['app-dialog-panel', options.customClass?.popup, options.customClass?.container].filter(
      (value): value is string => !!value
    );
    const ref = dialog.open<AppDialogComponent, AppDialogOptions, AppDialogResult>(AppDialogComponent, {
      data: options,
      width: appDialogWidth(options.width),
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: options.input ? false : 'dialog',
      panelClass,
    });
    const entry = { ref, component: ref.componentInstance };
    AppDialog.current = entry;

    ref.afterOpened().subscribe(() => {
      const popup = entry.component.popupElement;
      if (popup && options.didOpen) {
        options.didOpen(popup);
      }
    });
    if (options.didClose) {
      ref.afterClosed().subscribe(() => options.didClose?.());
    }

    return new Promise<AppDialogResult<T>>((resolve) => {
      ref.beforeClosed().subscribe((result) => {
        if (AppDialog.current === entry) {
          AppDialog.current = null;
        }
        resolve((result ?? DISMISSED) as AppDialogResult<T>);
      });
    });
  }

  /** Closes the open dialog, resolved as dismissed. */
  static close(): void {
    AppDialog.current?.component.dismiss();
  }

  static isVisible(): boolean {
    return AppDialog.current !== null;
  }

  /** Spinner in place of the buttons. With no dialog open, opens a blocking loading dialog. */
  static showLoading(_button?: HTMLElement | null): void {
    if (!AppDialog.current) {
      AppDialog.fire({ showConfirmButton: false, allowOutsideClick: false, allowEscapeKey: false });
    }
    AppDialog.current?.component.setLoading(true);
  }

  static hideLoading(): void {
    AppDialog.current?.component.setLoading(false);
  }

  static isLoading(): boolean {
    return AppDialog.current?.component.loading() ?? false;
  }

  static showValidationMessage(message: string): void {
    AppDialog.current?.component.showValidation(message);
  }

  static resetValidationMessage(): void {
    AppDialog.current?.component.showValidation(null);
  }

  static getPopup(): HTMLElement | null {
    return AppDialog.current?.component.popupElement ?? null;
  }

  static getContainer(): HTMLElement | null {
    const popup = AppDialog.getPopup();
    return (popup?.closest('.cdk-overlay-pane') as HTMLElement | null) ?? popup;
  }

  static getHtmlContainer(): HTMLElement | null {
    return AppDialog.current?.component.htmlContainerElement ?? null;
  }

  static getInput(): HTMLInputElement | null {
    return (AppDialog.current?.component.inputElement as HTMLInputElement | null) ?? null;
  }

  static getConfirmButton(): HTMLElement | null {
    return AppDialog.current?.component.confirmButtonElement ?? null;
  }

  static getDenyButton(): HTMLElement | null {
    return AppDialog.current?.component.denyButtonElement ?? null;
  }

  static getCancelButton(): HTMLElement | null {
    return AppDialog.current?.component.cancelButtonElement ?? null;
  }

  static getTimerLeft(): number | undefined {
    return AppDialog.current?.component.timerLeft();
  }

  /** `toast: true` — a short notice, as a snackbar: it must not block the page. */
  private static toast<T>(options: AppDialogOptions): Promise<AppDialogResult<T>> {
    const snackBar = AppDialog.injector!.get(MatSnackBar);
    const message = [options.title ?? options.titleText, options.text].filter((part) => !!part).join(' — ');
    const ref = snackBar.open(message || 'Done', undefined, { duration: options.timer || 3000 });
    return new Promise((resolve) => {
      ref.afterDismissed().subscribe(() => resolve({ ...DISMISSED, dismiss: AppDialogDismissReason.timer }));
    });
  }
}
